import React, { FunctionComponent, memo, useEffect, useRef } from 'react';
import { HubTableBar } from '../HubTableBar/HubTableBar';
import { SignatureField } from '../SignatureField/SignatureField';
import { totalNumberOfPackages } from './ControlSortList';
import { HubSortListOrderEntry } from '../../hooks/useFetchHubSortList';
import { ShowOrderScannerTable } from './ShowOrderScannerTable';
import { CellMeasurerCache, List } from 'react-virtualized';
import _ from 'lodash';

interface SortListTableProps {
  bundleId: string;
  shelfId: string;
  allBundleOrders: HubSortListOrderEntry[];
  courierName: string;
  bundleOrders: HubSortListOrderEntry[];
  updateHubSortListEntries: (bundlesId: string[]) => Promise<void>;
  cache: CellMeasurerCache;
  listRef: List;
  virtualizationIndex: number;
  onlyRemaining?: boolean;
}

const checkProps = (
  prevProps: Readonly<SortListTableProps>,
  nextProps: Readonly<SortListTableProps>
) =>
  !!(
    _.isEqual(prevProps.bundleOrders, nextProps.bundleOrders) &&
    _.isEqual(prevProps.allBundleOrders, nextProps.allBundleOrders) &&
    _.isEqual(prevProps.cache, nextProps.cache) &&
    nextProps.cache.has(nextProps.virtualizationIndex, 0) &&
    prevProps.bundleId === nextProps.bundleId &&
    prevProps.courierName === nextProps.courierName &&
    prevProps.shelfId === nextProps.shelfId
  );

const SortListTable: FunctionComponent<SortListTableProps> = memo(props => {
  const measureReference = useRef<HTMLDivElement>(null);
  const {
    bundleId,
    shelfId,
    allBundleOrders,
    courierName,
    bundleOrders,
    updateHubSortListEntries,
    cache,
    listRef,
    virtualizationIndex,
    onlyRemaining = false,
  } = props;

  useEffect(() => {
    if (
      cache &&
      listRef &&
      measureReference &&
      measureReference.current &&
      (measureReference.current.offsetHeight + 10 !==
        cache.getHeight(virtualizationIndex, 0) ||
        !cache.has(virtualizationIndex, 0))
    ) {
      cache.set(
        virtualizationIndex,
        0,
        measureReference.current.offsetWidth,
        measureReference.current.offsetHeight + 10
      );
      listRef.recomputeRowHeights(virtualizationIndex);
    }
  });

  return (
    <div className="sort-list__page" key={bundleId} ref={measureReference}>
      <div className="sort-list__page__header">
        <HubTableBar
          shelfId={shelfId}
          bundleId={bundleId}
          scannedOrders={allBundleOrders}
          courierName={courierName}
        />
      </div>
      <div className="sort-list__pageT_table">
        <ShowOrderScannerTable
          onlyRemaining={onlyRemaining}
          bundleOrders={bundleOrders}
          bundleId={bundleId}
          updateHubSortListEntries={updateHubSortListEntries}
          cache={cache}
          virtualizationIndex={virtualizationIndex}
          listRef={listRef}
        />
      </div>
      <div className="sort-list__page__footer">
        <SignatureField
          sumParcels={totalNumberOfPackages(bundleOrders)}
          nrOfOrders={bundleOrders.length}
        />
      </div>
    </div>
  );
}, checkProps);

SortListTable.displayName = 'SortListTable';
export { SortListTable };
